/**
 * Hook: Facility form state and validation.
 * Single responsibility: form data, touched, errors, change/blur, validate all, is valid.
 * Open/Closed: validators come from validationSchemas (injected via fieldValidators).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fieldValidators } from '../../../utils/validationSchemas';
import {
  parseFacilityToFormData,
  normalizeInputValue,
  getEmptyFormData,
} from '../utils/facilityFormUtils';

const VALIDATOR_MAP = {
  name: fieldValidators.facilityName,
  street: fieldValidators.street,
  city: fieldValidators.city,
  state: fieldValidators.state,
  zipCode: fieldValidators.zipCode,
};

function formDataEquals(a, b) {
  if (!a || !b) return a === b;
  const trim = (v) => String(v ?? '').trim();
  return (
    trim(a.name) === trim(b.name) &&
    trim(a.street) === trim(b.street) &&
    trim(a.city) === trim(b.city) &&
    trim(a.state).toUpperCase() === trim(b.state).toUpperCase() &&
    trim(a.zipCode) === trim(b.zipCode)
  );
}

export function useFacilityForm(facilityToEdit, isOpen) {
  const [formData, setFormData] = useState(getEmptyFormData);
  const [initialFormData, setInitialFormData] = useState(getEmptyFormData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    const data = parseFacilityToFormData(facilityToEdit);
    setFormData(data);
    setInitialFormData(data);
    setErrors({});
    setTouched({});
  }, [facilityToEdit, isOpen]);

  const hasChanges = useMemo(() => {
    if (!facilityToEdit) return true;
    return !formDataEquals(formData, initialFormData);
  }, [facilityToEdit, formData, initialFormData]);

  const validateField = useCallback((field, value) => {
    const validator = VALIDATOR_MAP[field];
    const error = validator ? validator(value) : null;
    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  }, []);

  const handleInputChange = useCallback(
    (field) => (e) => {
      const raw = e.target.value;
      const value = normalizeInputValue(field, raw);
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (touched[field]) {
        validateField(field, value);
      }
    },
    [touched, validateField],
  );

  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      validateField(field, formData[field]);
    },
    [formData, validateField],
  );

  const validateAll = useCallback(() => {
    const newTouched = {};
    const newErrors = {};
    let isValid = true;
    Object.keys(formData).forEach((field) => {
      newTouched[field] = true;
      const validator = VALIDATOR_MAP[field];
      const error = validator ? validator(formData[field]) : null;
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });
    setTouched(newTouched);
    setErrors(newErrors);
    return isValid;
  }, [formData]);

  const isFormValid = useCallback(() => {
    const allFilled = Object.values(formData).every((v) => String(v).trim() !== '');
    const allValid = Object.keys(formData).every((field) => {
      const validator = VALIDATOR_MAP[field];
      if (!validator) return true;
      return !validator(formData[field]);
    });
    return allFilled && allValid;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(getEmptyFormData());
    setErrors({});
    setTouched({});
  }, []);

  const getSubmitPayload = useCallback(() => ({
    name: formData.name,
    street: formData.street,
    city: formData.city,
    state: formData.state.toUpperCase(),
    zipCode: formData.zipCode,
  }), [formData]);

  return {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateAll,
    isFormValid,
    hasChanges,
    resetForm,
    getSubmitPayload,
  };
}
